// Tipos del API · dominio: Solicitudes de pago (Sprint 1): CFDI, flujo, póliza, chat, documentos.
// (dividido de lib/types.ts · F2 · se re-exporta por lib/types/index.ts)

import type { Paginado } from "./common";

// === Sprint 1 · Solicitudes de Pago ===

export type TipoSolicitud = 1 | 2 | 3;
export const TipoSolicitudValores = {
  FB60: 1 as TipoSolicitud,
  ContratoAnualizado: 2 as TipoSolicitud,
  OrdenDeCompra: 3 as TipoSolicitud,
} as const;

export interface SolicitudResumen {
  id: string;
  numeroSolicitud: number;
  /**
   * Folio formateado para mostrar al usuario:
   * - Con organización confirmada: 4 chars (NumeroSociedad) + 11 chars = 15 chars
   *   (ej. "5300" + "00000000123" → "530000000000123")
   * - Sin organización confirmada pero con CFDI cuyo RFC receptor está en el catálogo
   *   de organizaciones: esa sociedad + 11 chars (decisión del usuario, 30/09/2026)
   * - Sin ninguna de las dos: 11 dígitos (ej. "00000000123")
   */
  folio: string;
  estado: string;
  fechaInicio: string;
}

export interface OrganizacionEnSolicitud {
  id: string;
  rfc: string;
  numeroSociedad: string;
  nombre: string;
}

/** P44 · FB60 Contrapago · catálogo de organizaciones (`GET /catalogos/organizaciones`). */
export interface OrganizacionCatalogo {
  id: string;
  rfc: string;
  numeroSociedad: string;
  nombre: string;
  estaActiva: boolean;
}

export interface CFDIDeSolicitud {
  id: string;
  numeroSlot: number;
  uuid: string;
  /** Folio del CFDI · usado para empatar la dispersión multi-factura por Excel. */
  folio: string;
  version: string;
  tipoComprobante: string;
  moneda: string;
  fechaEmision: string;
  fechaTimbrado: string | null;
  total: number;
  subtotal: number;
  rfcEmisor: string;
  nombreEmisor: string;
  rfcReceptor: string;
  nombreReceptor: string;
  cargadoAt: string;
  statusFiscal: string;
  statusFiscalCodigo: string | null;
  statusFiscalMensajeOriginal: string | null;
  statusFiscalMensajeAmigable: string | null;
  statusFiscalConsultadoAt: string | null;
  // Detalle enriquecido de la consulta al SAT (vigencia · cancelación · EFOS).
  // Presente solo cuando hay una consulta registrada; "Desconocido" cuando fue con el mock.
  satProvider: string | null;
  satEstadoComprobante: string | null;
  satEsCancelable: string | null;
  satEstatusCancelacion: string | null;
  satValidacionEfos: string | null;
  satConsultadoAt: string | null;
}

export interface ReglaError {
  codigo: string;
  mensaje: string;
  /** D-10 · elemento de catálogo cuya falta provoca el error (p. ej. "EquivalenciaUnidad"). */
  catalogoFaltanteTipo?: string | null;
  /** Clave del elemento faltante (equivalencia de unidad: «SAT|SAP»). */
  catalogoFaltanteClave?: string | null;
}

export interface DatosGenerales {
  motivoPago: string;
  periodoServicioInicio: string;
  periodoServicioFin: string;
  comentarios: string | null;
  areaId: string;
  areaNombre: string;
}

export interface ProveedorAsignado {
  numeroProveedor: number;
  rfc: string;
  razonSocial: string;
}

/** Contrato Anualizado (CA) · snapshot del contrato anualizado asociado a la solicitud. */
export interface ContratoAnualizado {
  clave: string;
  proveedorRfc: string;
  proveedorNombre: string;
  proveedorNumero: number;
  total: number;
  moneda: string;
}

/** Orden de Compra (OC) · snapshot de la orden de compra asociada a la solicitud. */
export interface OrdenDeCompra {
  clave: string;
  proveedorRfc: string;
  proveedorNombre: string;
  proveedorNumero: number;
  total: number;
  moneda: string;
}

/** Equivalencia de unidad SAT↔SAP · catálogo admin para la conciliación de OC. */
export interface EquivalenciaUnidad {
  id: string;
  claveUnidadSat: string;
  unidadSap: string;
  descripcion: string | null;
  activo: boolean;
}

/** Ajuste de configuración de la app (tabla administracion.configuraciones). */
/** Tipo de dato del valor de una Configuracion · determina el control del gestor admin. */
export type TipoAjusteConfiguracion =
  | "Texto"
  | "Entero"
  | "Decimal"
  | "Booleano"
  | "Fecha"
  | "FechaHora"
  | "Secreto"
  /** CSV de números de proveedor del catálogo · selector múltiple de proveedores. */
  | "ListaProveedores";

export interface Configuracion {
  clave: string;
  valor: string;
  categoria: string;
  descripcion: string | null;
  valorDefault: string | null;
  tipo: TipoAjusteConfiguracion;
  actualizadoAt: string;
}

export interface SolicitudDetalle {
  /** El prorrateo lo armó y confirmó el sistema (prorrateo automático) · el wizard oculta el paso. */
  prorrateoAutomatico?: boolean;
  id: string;
  numeroSolicitud: number;
  /**
   * Folio formateado para mostrar al usuario · ver doc en `SolicitudResumen.folio`.
   * Backend computa: con sociedad (org confirmada o, de respaldo, la del RFC receptor
   * del CFDI) → 15 chars (sociedad + folio); sin sociedad → 11 chars.
   */
  folio: string;
  solicitanteId: string;
  solicitanteNombre: string | null;
  tipo: string | null;
  subtipo: string | null;
  estado: string;
  fechaInicio: string;
  fechaUltimoEvento: string;
  organizacion: OrganizacionEnSolicitud | null;
  cfdi: CFDIDeSolicitud | null;
  cfdis: CFDIDeSolicitud[];
  erroresReglas: ReglaError[];
  datosGenerales: DatosGenerales | null;
  proveedor: ProveedorAsignado | null;
  flujo: FlujoDeAutorizacion | null;
  ultimoMotivoRechazo: string[];
  motivosBaja: string[];
  /**
   * Nombre del usuario de Mesa de Control que dio de baja la solicitud ·
   * visible al solicitante junto con los motivos (decisión 38). Null si no
   * fue dada de baja o si terminó por otra vía (ej. cancelación propia).
   */
  dadaDeBajaPorNombre: string | null;
  /** Momento de la baja por Mesa de Control · null si no aplica. */
  dadaDeBajaAt: string | null;
  enviadaAContabilizacion: boolean;
  enviadaAContabilizacionAt: string | null;
  nombreArchivoSftp: string | null;
  contratoAnualizado: ContratoAnualizado | null;
  ordenCompra: OrdenDeCompra | null;
  /** Fecha de pago (ISO `YYYY-MM-DD`) fijada por Mesa de Control al autorizar el último nivel. */
  fechaPago: string | null;
  /**
   * P44 · FB60 Contrapago · "en espera de CFDI" es PARALELO al `estado`, igual
   * patrón que `enviadaAContabilizacion`. `esperaCfdiFechaLimite` no-nulo indica
   * que la solicitud entró en espera; `esperaCfdiConciliadoAt` no-nulo indica que
   * ya se resolvió (el CFDI real llegó y pasó las reglas de conciliación).
   */
  esperaCfdiFechaLimite: string | null;
  esperaCfdiAvisoEnviadoAt: string | null;
  esperaCfdiVencida: boolean;
  esperaCfdiConciliadoAt: string | null;
  /** Subtipo INVOICE · datos extraídos por OCR de la factura extranjera. Null si no es Invoice. */
  invoice?: DatosInvoiceDetalle | null;
}

/** Subtipo INVOICE · datos extraídos de la factura extranjera (PDF). */
export interface DatosInvoiceDetalle {
  plantilla: string;
  emisorNombre: string;
  emisorTaxId: string | null;
  emisorDireccion: string | null;
  fechaExpedicion: string | null;
  receptorNombre: string | null;
  receptorRfc: string;
  numeroFactura: string | null;
  importeTotal: number;
  moneda: string;
  impuestoImporte: number | null;
  impuestoTasa: number | null;
  conceptos: { descripcion: string; importe: number }[];
  pdfPath: string;
  /** Total de impuestos retenidos extraído por OCR · null si no se reportó (o invoice previo al campo). */
  retencionesImporte?: number | null;
}

export interface NivelDeFlujo {
  id: string;
  nivel: number;
  rolesAutorizadores: string[];
  estado: "Pendiente" | "EnProceso" | "Autorizado";
  iniciadoAt: string | null;
  autorizadoAt: string | null;
  autorizadoPorUsuarioId: string | null;
  autorizadoPorEmail: string | null;
  autorizadoPorNombre: string | null;
}

export interface FlujoDeAutorizacion {
  id: string;
  nivelActual: number;
  cantidadNiveles: number;
  estado: "Resuelto" | "EnProceso" | "Completado";
  resueltoAt: string;
  completadoAt: string | null;
  niveles: NivelDeFlujo[];
}

export interface BandejaAutorizacionItem {
  id: string;
  numeroSolicitud: number;
  /** Folio formateado · ver doc en `SolicitudResumen.folio` (15 chars con sociedad, 11 sin). */
  folio: string;
  estado: string;
  tipo: string | null;
  subtipo: string | null;
  organizacionNombre: string | null;
  areaNombre: string | null;
  proveedorRazonSocial: string | null;
  proveedorNumero: number | null;
  rfcEmisor: string | null;
  descripcionPrimerConcepto: string | null;
  totalCfdi: number | null;
  monedaCfdi: string | null;
  nivelActual: number;
  cantidadNiveles: number;
  fechaCreacion: string;
  fechaUltimoEvento: string;
}

/** Página del listado de la bandeja · espejo de BandejaPageDto del backend. */
export type BandejaAutorizacionPage = Paginado<BandejaAutorizacionItem>;

export interface LineaPolizaApi {
  numeroLinea: number;
  claveSat: string;
  descripcion: string;
  centroDeCostoCodigo: string;
  cuentaContableCodigo: string;
  importe: number;
}

export interface PolizaApi {
  id: string;
  numeroSolicitud: number;
  numeroCfdi: number;
  cfdiUuid: string;
  numeroProveedor: number;
  razonSocialProveedor: string;
  numeroSociedad: string;
  organizacionNombre: string;
  totalDebe: number;
  moneda: string;
  estado: "Generada" | "DepositadaEnSftp" | "ErrorEnDeposito";
  nombreArchivoSftp: string | null;
  errorDeposito: string | null;
  generadaAt: string;
  depositadaAt: string | null;
  lineas: LineaPolizaApi[];
  /** Causa del error de depósito (null sin error). Solo `Deposito` es reintentable. */
  motivoError?: "Deposito" | "BloqueoFiscal" | "DatosInvalidos" | "SolicitudDadaDeBaja" | null;
  /** Intentos de depósito fallidos (automáticos + manuales). */
  intentosDeposito?: number;
  /** Siguiente reintento automático (ISO) · null si ya no hay. */
  proximoReintentoAt?: string | null;
  /** true si aplica «Reintentar depósito» (el permiso lo decide la UI). */
  puedeReintentarDeposito?: boolean;
  /** Hotspots 53/109 · true si aplica «Regenerar póliza» (error sin TXT o datos inválidos). */
  puedeRegenerarse?: boolean;
}

export interface MiSolicitud {
  id: string;
  numeroSolicitud: number;
  /** Folio formateado · ver `SolicitudResumen.folio`. */
  folio: string;
  estado: string;
  tipo: string | null;
  subtipo: string | null;
  organizacionNombre: string | null;
  /** Área a la que pertenece · null hasta que se capturan datos generales (paso 3). */
  areaNombre: string | null;
  /** Razón social del proveedor asignado · null hasta el paso 4 del wizard. */
  proveedorRazonSocial: string | null;
  /** Número de proveedor (catálogo) · null hasta el paso 4. */
  proveedorNumero: number | null;
  /** RFC del proveedor asignado (snapshot del catálogo) · null hasta el paso 4. */
  proveedorRfc: string | null;
  /**
   * Descripción del primer concepto del XML del CFDI cargado · backend la
   * envía completa (hasta 500 chars). El frontend la trunca a 25 chars para
   * la columna del listado con tooltip para ver completa.
   */
  descripcionPrimerConcepto: string | null;
  totalCfdi: number | null;
  monedaCfdi: string | null;
  rfcEmisor: string | null;
  cantidadDocumentos: number | null;
  flujoNivelActual: number | null;
  flujoCantidadNiveles: number | null;
  /** Roles autorizadores del nivel actual (CSV) · para etiquetar el estado. */
  flujoRolActual: string | null;
  tieneRechazoPendiente: boolean;
  enviadaAContabilizacion: boolean;
  fechaInicio: string;
  fechaUltimoEvento: string;
  /** Fecha de pago (ISO `YYYY-MM-DD`) · null hasta que la solicitud queda Autorizada. */
  fechaPago: string | null;
  /**
   * Nombre del solicitante dueño · solo viene resuelto (backend vía
   * `IUserDirectory`) cuando el usuario actual ve solicitudes de otros
   * (Mesa de Control/Auditor/Administrador). Null para el propio
   * solicitante viendo sus solicitudes.
   */
  solicitanteNombre: string | null;
  /** Hotspot 90 · la solicitud es del usuario actual (continuar/cancelar exigen ser el dueño). */
  esPropia?: boolean;
  /**
   * Número de facturas (UUIDs con acuse de validación) · la fila solo muestra el chevron
   * para expandir sus facturas cuando es mayor a cero.
   */
  numeroFacturas?: number;
  /**
   * Columna «CA/OC» · clave del contrato anualizado (tipo CA) o de la orden de compra
   * (tipo OC) asignada a la solicitud; null en FB60 (no aplica) o si aún no se asoció.
   */
  claveCaOc?: string | null;
  /** Subtipo INVOICE (FB60/CA/OC) con la factura extranjera ya cargada (datos + PDF). */
  tieneInvoice?: boolean;
  /**
   * Qué muestra la fila expandible (backend · regla única): "Invoice" (datos + PDF de la
   * factura extranjera), "Facturas" (CFDIs con acuse) o null (nada que mostrar → sin chevron).
   */
  tipoDetalle?: TipoDetalleSolicitud | null;
}

/** Contenido de la fila expandible de «Mis solicitudes de pago». */
export type TipoDetalleSolicitud = "Facturas" | "Invoice";

export interface MensajeDeChat {
  id: string;
  solicitudId: string;
  autorUsuarioId: string;
  rolAutor: "Solicitante" | "Autorizador" | "MesaDeControl";
  texto: string;
  nivelDelFlujo: number | null;
  enviadoAt: string;
}

/**
 * Respuesta del GET del chat · hotspot 95: `puedeEscribir` lo calcula el backend con
 * la MISMA regla que valida el envío (PoliticaAccesoSolicitud), así el cuadro de
 * escribir solo aparece a quien realmente puede enviar.
 */
export interface ChatDeSolicitud {
  mensajes: MensajeDeChat[];
  puedeEscribir: boolean;
}

/**
 * Código de categoría de documento · catálogo CREABLE (Gestión Documental fase 2): las de sistema
 * se listan para autocompletado, pero puede ser cualquier código configurado por el administrador.
 */
export type CategoriaDocumento =
  | (string & {})
  | "FacturaXml"
  | "FacturaPdf"
  | "Evidencia"
  | "Contrato"
  | "Adicional"
  | "CertificadoResidencia"
  /** Gestión Documental (D-2) · PDF del Invoice · lo registra el servidor al cargar la factura. */
  | "FacturaInvoice";

/** Documento de la solicitud (Gestión Documental · fuente única) en la forma que pintan las filas. */
export interface DocumentoVinculado {
  id: string;
  documentoId: string;
  categoria: string;
  nombreOriginal: string;
  vinculadoAt: string;
  /** Tipo del catálogo (P31) · solo para la categoría Adicional. */
  tipoDocumento?: string | null;
  /** Gestión Documental (fase 2) · CFDI al que pertenece (requisitos «por CFDI»). */
  numeroCfdi?: number;
}

/** Tipo de documento ADICIONAL del catálogo (P31 · /api/catalogos/tipos-documento). */
export interface TipoDocumentoAdicional {
  id: string;
  nombre: string;
  orden: number;
  activo: boolean;
}

export interface DocumentoApi {
  id: string;
  solicitudId: string;
  numeroSolicitud: number;
  numeroCfdi: number;
  categoria: string;
  nombreOriginal: string;
  extension: string;
  tamanoBytes: number;
  rutaStorage: string;
  subidoPorUsuarioId: string;
  cargadoAt: string;
  /** Tipo del catálogo (P31) · solo para la categoría Adicional. */
  tipoDocumento?: string | null;
  /** Gestión Documental · versión vigente (1 = nunca reemplazado). */
  versionActual: number;
  /** Gestión Documental · el XML/PDF del CFDI no se reemplazan desde aquí. */
  esReemplazable: boolean;
}

export interface CargarCFDIResultado {
  solicitud: SolicitudDetalle;
  reglasAutorizadas: boolean;
  erroresReglas: ReglaError[];
}
